import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import {
  fetchAdminIntegrationHealth,
  fetchAdminIntegrations,
  fetchAdminIntegrationInstallations,
  fetchAdminIntegrationWebhooks,
} from '../../../services/Http/AdminApi';

const AdminIntegrationsOverview = () => {
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);
  const [health, setHealth] = useState(null);
  const [installations, setInstallations] = useState(null);
  const [integrators, setIntegrators] = useState([]);
  const [webhooks, setWebhooks] = useState([]);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const [healthResp, installationsResp, listResp, webhooksResp] = await Promise.all([
        fetchAdminIntegrationHealth(),
        fetchAdminIntegrationInstallations(),
        fetchAdminIntegrations({ per_page: 10 }),
        fetchAdminIntegrationWebhooks(),
      ]);
      if (request !== loadRequest.current) return;
      setHealth(healthResp?.data || null);
      setInstallations(installationsResp?.data || null);
      setIntegrators(listResp?.data || []);
      setWebhooks((webhooksResp?.data || []).slice(0, 10));
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load integrations overview');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { loadRequest.current += 1; };
  }, [load]);

  const failingWebhooks = useMemo(
    () => (webhooks || []).filter((w) => Number(w.failure_count || 0) > 0 || ['failed', 'timeout'].includes(w.last_delivery_status)).length,
    [webhooks]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Integrations Control Center</PageTitle>
      </PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <FilterRow>
            <FreshnessBadge status={fetchStatus} sourceType="operational" />
          </FilterRow>
          <FilterRow>
            <NavLink to="/integrations/list">All Integrators</NavLink>
            <NavLink to="/integrations/webhooks">Webhook Health</NavLink>
            <NavLink to="/integrations/logs">Logs</NavLink>
            <NavLink to="/integrations/sync">Sync</NavLink>
            <NavLink to="/integrations/quotas">Quotas</NavLink>
            <MainButton size="small" onClick={load}>Refresh</MainButton>
          </FilterRow>
        </AdminFilterBar>

        {fetchStatus === 'failed' ? <p role="alert">Failed to load integrations overview</p> : <>
        <StatsGrid>
          <StatCard icon="I" title="Integrators" value={fmtNum(health?.total_integrators)} color="transactions" />
          <StatCard icon="A" title="Active" value={fmtNum(health?.active_integrators)} color="success" />
          <StatCard icon="W" title="Webhooks Failing" value={fmtNum(health?.webhooks_failing ?? failingWebhooks)} color="warning" />
          <StatCard icon="E" title="Errors (24h)" value={fmtNum(health?.errors_24h)} color="danger" />
          <StatCard icon="S" title="Sync Running" value={fmtNum(health?.sync_running)} color="info" />
          <StatCard icon="L" title="Logs (24h)" value={fmtNum(health?.logs_24h)} color="totalVolume" />
        </StatsGrid>

        <Section>
          <SectionTitle>Installation Footprint</SectionTitle>
          <StatsGrid>
            <StatCard icon="SW" title="Shopware Installs" value={fmtNum(installations?.shopware_installations)} color="info" />
            <StatCard icon="SH" title="Shopify Shops" value={fmtNum(installations?.shopify_shops)} color="transactions" />
            <StatCard icon="WC" title="Woo Integrators" value={fmtNum(installations?.woocommerce_integrators)} color="warning" />
            <StatCard icon="CU" title="Custom Integrators" value={fmtNum(installations?.custom_integrators)} color="revenueValue" />
          </StatsGrid>
        </Section>

        <Section>
          <SectionTitle>Plugin installations and onboarding</SectionTitle>
          <p>Store URLs reported by plugins are unverified and are not proof of domain ownership.</p>
          <AdminDataTable
            loading={loading}
            rows={installations?.plugin_installations || []}
            emptyText="No plugin installation reports yet"
            columns={[
              { key: 'platform', label: 'Plugin' },
              { key: 'plugin_version', label: 'Version' },
              { key: 'last_event', label: 'Last event', render: (r) => r.last_event ? `${r.last_event} · ${formatDate(r.last_event_at)}` : '—' },
              { key: 'store_name', label: 'Store', render: (r) => r.store_name || r.store_url || '—' },
              { key: 'organization_name', label: 'Organization', render: (r) => r.organization_name || 'Not registered' },
              { key: 'status', label: 'Onboarding status', render: (r) => <StatusBadge $tone={statusTone(r.status)}>{r.status}</StatusBadge> },
              { key: 'installed_at', label: 'Installed', render: (r) => formatDate(r.installed_at) },
              { key: 'last_seen_at', label: 'Last report', render: (r) => formatDate(r.last_seen_at) },
            ]}
          />
        </Section>

        <Section>
          <SectionTitle>Recently Updated Integrators</SectionTitle>
          <AdminDataTable
            loading={loading}
            rows={integrators}
            emptyText="No integrators found"
            columns={[
              { key: 'name', label: 'Integrator', render: (r) => <RowLink to={`/integrations/${r.id}`}>{r.name || '—'}</RowLink> },
              { key: 'organization_name', label: 'Organization' },
              { key: 'integration_type', label: 'Type' },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge $tone={statusTone(r.status)}>{r.status || 'unknown'}</StatusBadge> },
              { key: 'webhook_health', label: 'Webhook', render: (r) => <StatusBadge $tone={statusTone(r.webhook_health)}>{r.webhook_health || 'unknown'}</StatusBadge> },
              { key: 'updated_at', label: 'Updated', render: (r) => formatDate(r.updated_at) },
            ]}
          />
        </Section>

        <Section>
          <SectionTitle>Webhook Issues (Top 10)</SectionTitle>
          <AdminDataTable
            loading={loading}
            rows={webhooks}
            emptyText="No webhook records found"
            columns={[
              { key: 'integrator_name', label: 'Integrator' },
              { key: 'organization_name', label: 'Organization' },
              { key: 'last_delivery_status', label: 'Last Status', render: (r) => <StatusBadge $tone={statusTone(r.last_delivery_status)}>{r.last_delivery_status || '—'}</StatusBadge> },
              { key: 'failure_count', label: 'Failures' },
              { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
              { key: 'last_delivery_at', label: 'Last Delivery', render: (r) => formatDate(r.last_delivery_at) },
            ]}
          />
        </Section>
        </>}
      </PageContent>
    </PageContainer>
  );
};

export default AdminIntegrationsOverview;

const fmtNum = (v) => Number(v || 0).toLocaleString();
const formatDate = (v) => (v ? new Date(v).toLocaleString() : '—');
const statusTone = (status) => {
  const value = String(status || '').toLowerCase();
  if (['healthy', 'success', 'ok', 'active'].includes(value)) return 'success';
  if (['failed', 'timeout', 'error'].includes(value)) return 'danger';
  if (['stale', 'missing', 'inactive'].includes(value)) return 'warning';
  return 'neutral';
};

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
`;
const Section = styled.section`margin-top: 1.25rem;`;
const SectionTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1rem;
`;
const NavLink = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
`;
const RowLink = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 600;
`;
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: capitalize;
  background: ${({ theme, $tone }) => ({
    success: theme.colors.successBg,
    warning: theme.colors.warningBg,
    danger: theme.colors.dangerBg,
    neutral: theme.colors.surfaceMuted,
  }[$tone] || theme.colors.surfaceMuted)};
  color: ${({ theme, $tone }) => ({
    success: theme.colors.success,
    warning: theme.colors.warning,
    danger: theme.colors.danger,
    neutral: theme.colors.textMuted,
  }[$tone] || theme.colors.textMuted)};
`;
